import {useMemo} from 'react';

import {useHistoryKeysQuery} from '../../../../state/graphql/historyKeysQuery';
import {useHistoryKeyInfoQueryVars} from '../../../../state/runs/hooks';
import {
  EMPTY_HISTORY_KEY_INFO,
  type RunHistoryKeyType,
} from '../../../../types/run';
import {keyInfoToDefaultPanelSpecs} from '../../../../util/panelbank';
import {useIsNgwEnabled} from '../../../../util/rampFeatureFlags/rampFeatureFlags';
import {useDefinedMetricContext} from '../../../PanelBank/contextProviders/DefinedMetricContext';
import {useWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';
import {useAutoOrganizePrefix} from '../../Settings/hooks';
import {type QuickAddKeys} from '../PanelsQuickAddContext';
import {useSearchedKeys} from './useSearchedKeys';

const NUMBER_KEY_TYPE: RunHistoryKeyType = 'number';

export type MultiMetricSearchedKeys = {
  searchedKeys: QuickAddKeys;
  isLoading: boolean;
  error?: Error;
};

/**
 * Returns all number-type metric keys matching `regex`,
 * for the multi-metric quick add tab.
 *
 * Multi-metric quick add matches the regex against the whole metric set
 * client-side, then groups the matches into panels by capture group.
 * This requires having the full key set, which is not available in NGW
 * where the keys are sampled/truncated.
 *
 * This hook runs when the multi-metric quick add tab is mounted,
 * and it fetch the full history key set on-demand if NGW is enabled.
 */
export const useMultiMetricSearchedKeys = (
  regex: string,
  isRegexValid: boolean
): MultiMetricSearchedKeys => {
  const isNgwEnabled = useIsNgwEnabled();

  // non-ngw/legacy path: search for matching keys in the expected panel
  // specs (derived from KeyInfoQueryContext)
  const legacySearchedKeys = useSearchedKeys(
    regex,
    NUMBER_KEY_TYPE,
    isRegexValid && !isNgwEnabled
  );
  const legacyKeysResult = useMemo(
    () => ({
      searchedKeys: legacySearchedKeys,
      isLoading: false,
      error: undefined,
    }),
    [legacySearchedKeys]
  );

  // ngw path: fetch full history key set on-demand
  const onDemandKeysResult = useFullHistoryNumberKeys(regex, isNgwEnabled);

  return isNgwEnabled ? onDemandKeysResult : legacyKeysResult;
};

function useFullHistoryNumberKeys(
  regex: string,
  enabled: boolean
): MultiMetricSearchedKeys {
  const {runSetRefs, customRunNamesRef} = useWorkspaceRefsContext();
  const autoOrganizePrefix = useAutoOrganizePrefix();
  const {
    definedMetricsMap,
    hiddenMetrics,
    isLoading: definedMetricsLoading,
  } = useDefinedMetricContext();

  const queryVars = useHistoryKeyInfoQueryVars(
    runSetRefs,
    customRunNamesRef,
    !enabled
  );
  // Fetch transiently: this gets the full key set for the project, which
  // can be a huge payload. Retaining it (caching & polling) even after the user
  // exits MMQA can use up GBs of browser memory and even freeze the tab.
  const query = useHistoryKeysQuery(queryVars, !enabled, {transient: true});

  const historyKeyInfo =
    enabled &&
    !query.loading &&
    query.error == null &&
    query.historyKeyInfo != null
      ? query.historyKeyInfo
      : EMPTY_HISTORY_KEY_INFO;

  const allNumberKeys = useMemo<QuickAddKeys>(() => {
    const specs = keyInfoToDefaultPanelSpecs(
      historyKeyInfo.keys,
      {},
      autoOrganizePrefix,
      definedMetricsMap,
      hiddenMetrics
    );
    return Object.entries(specs)
      .filter(([, spec]) => spec.keyType === NUMBER_KEY_TYPE)
      .sort((a, b) => a[0].localeCompare(b[0]));
  }, [historyKeyInfo, autoOrganizePrefix, definedMetricsMap, hiddenMetrics]);

  const searchedKeys = useMemo<QuickAddKeys>(() => {
    if (!regex) {
      return allNumberKeys;
    }
    try {
      const compiled = new RegExp(regex, 'i');
      return allNumberKeys.filter(([key]) => compiled.test(key));
    } catch {
      return [];
    }
  }, [allNumberKeys, regex]);

  return useMemo(
    () => ({
      searchedKeys,
      isLoading: enabled && (query.loading || definedMetricsLoading),
      error: query.error ?? undefined,
    }),
    [searchedKeys, enabled, query.loading, query.error, definedMetricsLoading]
  );
}
